// ════════════════════════════════════════════════════════════════════════════
//  PDV Types — T2.1 PDV MVP
// ════════════════════════════════════════════════════════════════════════════

export enum StatusPdvVenda {
  EmAndamento = 1,
  Finalizada  = 2,
  Cancelada   = 3,
}

export enum FormaPagamentoPdv {
  Dinheiro      = 1,
  CartaoDebito  = 2,
  CartaoCredito = 3,
  Pix           = 4,
  Voucher       = 5,
}

export const FormaPagamentoLabel: Record<FormaPagamentoPdv, string> = {
  [FormaPagamentoPdv.Dinheiro]:      'Dinheiro',
  [FormaPagamentoPdv.CartaoDebito]:  'Débito',
  [FormaPagamentoPdv.CartaoCredito]: 'Crédito',
  [FormaPagamentoPdv.Pix]:           'PIX',
  [FormaPagamentoPdv.Voucher]:       'Voucher',
};

// ── Response types ────────────────────────────────────────────────────────

export interface VendaItem {
  id: number;
  produtoId: number;
  subProdutoId?: number;
  quantidade: number;
  precoUnitario: number;
  desconto: number;
  total: number;
}

export interface VendaPagamento {
  id: number;
  formaPagamento: FormaPagamentoPdv;
  formaPagamentoNome: string;
  valor: number;
  parcelas: number;
  nsuCartao?: string;
  bandeira?: string;
}

export interface Venda {
  id: number;
  empresaId: number;
  operadorId: number;
  caixaId: number;
  clienteId?: number;
  dtAbertura: string;
  dtFechamento?: string;
  status: StatusPdvVenda;
  statusNome: string;
  totalBruto: number;
  totalDesconto: number;
  totalLiquido: number;
  troco: number;
  notaEletronicaId?: number;
  itens: VendaItem[];
  pagamentos: VendaPagamento[];
}

export interface ProdutoPdv {
  id: number;
  descricao: string;
  codigoBarras?: string;
  codigoInterno?: string;
  precoUnitario: number;
  estoqueAtual: number;
  permiteFracionado: boolean;
}

export interface ClientePdv {
  id: number;
  nome: string;
  cpfCnpj?: string;
  telefone?: string;
}

// ── Turno types (T2.3) ────────────────────────────────────────────────────

export enum StatusPdvTurno {
  Aberto               = 1,
  Fechado              = 2,
  ConciliacaoFechada   = 3,
}

export interface Turno {
  id: number;
  operadorId: number;
  caixaId: number;
  status: StatusPdvTurno;
  dtAbertura: string;
  dtFechamento?: string;
  fundoCaixa: number;
}

export interface TurnoResumo {
  turnoId: number;
  operadorId: number;
  caixaId: number;
  status: StatusPdvTurno;
  dtAbertura: string;
  dtFechamento?: string;
  fundoCaixa: number;
  esperadoDinheiro: number;
  esperadoDebito: number;
  esperadoCredito: number;
  esperadoPix: number;
  esperadoVoucher: number;
  totalSangrias: number;
  totalSuprimentos: number;
  contadoDinheiro: number;
  contadoDebito: number;
  contadoCredito: number;
  contadoPix: number;
  contadoVoucher: number;
  diferenca: number;
  observacao?: string;
}

export interface AbrirTurnoDto {
  empresaId: number;
  caixaId: number;
  fundoCaixa: number;
}

export interface FecharTurnoDto {
  contadoDinheiro: number;
  contadoDebito: number;
  contadoCredito: number;
  contadoPix: number;
  contadoVoucher: number;
  observacao?: string;
}

export interface MovimentoCaixaDto {
  valor: number;
  motivo?: string;
}

// ── NFC-e e Estorno (T2.4) ───────────────────────────────────────────────

export interface NfceDados {
  notaEletronicaId: number;
  numeroNota: number;
  chaveNota?: string;
  status?: string;
  qrCode?: string;
  urlConsulta?: string;
  contingencia: boolean;
}

export interface EstornoResultado {
  sucesso: boolean;
  statusNfce: string;
  estoqueEstornado: boolean;
  caixaEstornada: boolean;
  erros: string[];
}

// Espelha EstornoHistoricoDto (GET /api/pdv/estornos)
export interface EstornoResumoItem {
  vendaId: number;
  empresaId: number;
  operadorId: number;
  caixaId: number;
  clienteId?: number;
  dtAbertura: string;
  dtFechamento?: string;
  dtEstorno?: string;
  motivoEstorno?: string;
  totalLiquido: number;
  notaEletronicaId?: number;
  /** PDV-14: etapas do estorno que falharam (ex.: "nfce") — estorno incompleto */
  etapasComErro: string[];
}

// Espelha EstornoErroDto (GET /api/pdv/estornos/erros)
export interface EstornoErroItem {
  id: number;
  pdvVendaId: number;
  etapa: string;
  erro: string;
  motivo?: string;
  dtErro: string;
}

// ── PDV-07 — devolução fiscal de estorno fora do prazo ───────────────────

// Espelha EstornoPendenciaDto (GET /api/pdv/estornos/pendencias)
export interface EstornoPendencia {
  logId: number;
  pdvVendaId: number;
  erro: string;
  motivo?: string;
  dtErro: string;
  notaEletronicaId?: number;
  numeroNota: number;
  chaveNota?: string;
  totalNota: number;
  /** NFC-e segue autorizada na SEFAZ — a NF-e de devolução se aplica. */
  notaAutorizada: boolean;
}

// Espelha DevolucaoResultadoDto (POST /api/pdv/{id}/devolucao)
export interface DevolucaoResultado {
  sucesso: boolean;
  cStat?: string;
  xMotivo?: string;
  protocolo?: string;
  chaveNota?: string;
  notaDevolucaoId: number;
  numeroNota: number;
  reaproveitada: boolean;
}

// ── Conciliação de Caixa (T2.5) ──────────────────────────────────────────

export interface DivergenciaForma {
  formaPagamento: string;
  esperado: number;
  contado: number;
  diferenca: number;
  temDivergencia: boolean;
}

export interface AuditoriaPeriodo {
  dtInicio: string;
  dtFim: string;
  divergencias: DivergenciaForma[];
  temDivergencia: boolean;
}

export interface LancamentoConciliacao {
  dtMovimento: string;
  tipo: string;
  descricao: string;
  formaPagamento: string;
  valor: number;
  saldoAcumulado: number;
}

export interface RelatorioConciliacao {
  saldoInicial: number;
  totalEntradas: number;
  totalSaidas: number;
  saldoFinal: number;
  lancamentos: LancamentoConciliacao[];
}

export interface AjusteHistorico {
  id: number;
  turnoId: number;
  dtAjuste: string;
  operadorId: number;
  formaPagamento: string;
  valorAjuste: number;
  motivo: string;
  isFechado: boolean;
  status: string;
}

export interface AjustarDivergenciaDto {
  formaPagamento: string;
  valorAjuste: number;
  motivo: string;
}

export interface FecharConciliacaoDto {
  operadorId: number;
}

// ── Offline Sync (T2.6) ───────────────────────────────────────────────────

export interface VendaOfflineItemDto {
  produtoId: number;
  quantidade: number;
  precoUnitario: number;
  descricao: string;
  subProdutoId?: number;
}

export interface VendaOfflinePagamentoDto {
  formaPagamento: FormaPagamentoPdv;
  valor: number;
  parcelas?: number;
}

export interface VendaOfflineDto {
  idLocal: string;
  empresaId: number;
  caixaId: number;
  clienteId?: number;
  itens: VendaOfflineItemDto[];
  pagamentos: VendaOfflinePagamentoDto[];
  dtCriacao: string;
}

export interface SyncOfflineResultado {
  processadas: string[];
  conflitos:   { idLocal: string; motivo: string }[];
  erros:       { idLocal: string; mensagem: string }[];
}

// ── Request types ─────────────────────────────────────────────────────────

export interface AbrirVendaDto {
  empresaId: number;
  caixaId: number;
  clienteId?: number;
}

export interface AdicionarItemDto {
  produtoId: number;
  quantidade: number;
  subProdutoId?: number;
}

export interface AlterarQuantidadeDto {
  quantidade: number;
}

export interface AplicarDescontoDto {
  descontoItemValor?: number;
  itemId?: number;
  descontoTotalValor?: number;
}

export interface AdicionarPagamentoDto {
  formaPagamento: FormaPagamentoPdv;
  valor: number;
  parcelas?: number;
  nsuCartao?: string;
  bandeira?: string;
}
